import { toast } from 'vue-sonner';
import type { Router } from 'vue-router';

/**
 * Load the new build when a script file of this build fails to load.
 *
 * Each deploy deletes the previous build's files. A tab opened before the deploy still runs the
 * old build, so the first time it opens a screen it has not loaded yet, it asks for a file that is
 * gone and gets a 403. On staging on 2026-10-27, a tab on `index-kU0MDAKp.js` failed to load
 * `GuessTheTrackDialog-DxXaqZaX.js` after `index-PYG3rwRX.js` was deployed. Vite fires
 * `vite:preloadError` for exactly this case.
 *
 * When the file belongs to a screen the user is moving to, the page loads that screen's address
 * from the new build. The user is leaving the screen they were on, so nothing they typed there is
 * lost. When the file belongs to the screen they are on, such as a dialog, a reload would throw
 * away what they have typed, so they are asked instead.
 *
 * A reload that does not fix it (the file is missing from the new build too) must not loop, so
 * the time of the last reload is kept in sessionStorage, and a second failure within
 * {@link RELOAD_GUARD_MS} of it is left to surface as an error. Without sessionStorage there is
 * no way to tell a loop from a first failure, so the user is asked instead.
 */

/** A second failure this soon after a reload is not fixed by reloading again. */
export const RELOAD_GUARD_MS = 60_000;

const LAST_RELOAD_KEY = 'tabasco:stale-build-reloaded-at';

export interface ReloadOnStaleBuildOptions {
    target?: Pick<Window, 'addEventListener'>;
    /** The address a navigation in progress is heading to, or null when none is. */
    navigatingTo: () => string | null;
    reloadTo?: (url: string) => void;
    /** Asks the user to reload, when reloading by itself could lose their work or loop. */
    offerReload?: () => void;
    now?: () => number;
    storage?: Pick<Storage, 'getItem' | 'setItem'>;
}

function offerReloadInAToast(): void {
    toast.warning('A new version of Tabasco has been deployed.', {
        // One toast however many files fail.
        id: 'stale-build',
        duration: Infinity,
        description: 'Reload the page to finish opening this. Anything not saved on this page will be lost.',
        action: { label: 'Reload', onClick: () => window.location.reload() },
    });
}

export function installReloadOnStaleBuild({
    target = window,
    navigatingTo,
    reloadTo = (url) => window.location.assign(url),
    offerReload = offerReloadInAToast,
    now = Date.now,
    storage = sessionStorage,
}: ReloadOnStaleBuildOptions): void {
    target.addEventListener('vite:preloadError', (event: Event) => {
        const destination = navigatingTo();
        if (destination === null) {
            offerReload();
            return;
        }
        try {
            const lastReload = Number(storage.getItem(LAST_RELOAD_KEY) ?? 0);
            if (now() - lastReload < RELOAD_GUARD_MS) return;
            storage.setItem(LAST_RELOAD_KEY, String(now()));
        } catch {
            // Storage blocked: a reload could not tell whether it had already reloaded.
            offerReload();
            return;
        }
        // Stops Vite rethrowing the error while the page is going away.
        event.preventDefault();
        reloadTo(destination);
    });
}

/**
 * Where the router is heading while a navigation is in progress, for
 * {@link ReloadOnStaleBuildOptions.navigatingTo}. A route's screen file loads after the
 * `beforeEach` guards, so the address is known by the time that file fails.
 */
export function navigationTargetOf(router: Router): () => string | null {
    let destination: string | null = null;
    router.beforeEach((to) => {
        destination = router.resolve(to).href;
    });
    router.afterEach(() => {
        destination = null;
    });
    router.onError(() => {
        destination = null;
    });
    return () => destination;
}
